import React, { useState, useEffect } from 'react';
import { Play, CheckCircle, Clock, Loader2, Wrench, Check } from 'lucide-react';

import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';

import { useToast } from '../hooks/useToast';
import { client, fetchUtilProductionOrders, fetchProducedSoFar } from '../apiHelper';

export const ProductionFloorConsole = ({ storeId }) => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [workcenterOptions, setWorkcenterOptions] = useState([]);
    const [selectedWc, setSelectedWc] = useState('');
    const [activeOperations, setActiveOperations] = useState([]);
    const [executingOpId, setExecutingOpId] = useState(null);

    const [completionData, setCompletionData] = useState({
        actualSetupMinutes: '',
        actualRunMinutes: '',
        scrapQuantity: '0',
        notes: ''
    });

    const [productionOrderOptions, setProductionOrderOptions] = useState([]);
    const [selectedPo, setSelectedPo] = useState('');

    const [isLastOperation, setIsLastOperation] = useState(false);
    const [producedQuantity, setProducedQuantity] = useState('');
    const [producedSoFar, setProducedSoFar] = useState(0);

    // useEffect(() => {
    //     if (!storeId) return;
    //     const loadWorkcenters = async () => {
    //         try {
    //             const res = await client.fetchData(`/manufacturing/work-centers/${storeId}`);
    //             const list = Array.isArray(res) ? res : (res?.workcenters || []);
    //             setWorkcenterOptions(list.map(w => ({
    //                 label: `${w.name} - Cell #${w.workcenter_id}`,
    //                 value: w.workcenter_id.toString()
    //             })));
    //         } catch (err) {
    //             showToast("Could not retrieve floor work centers.", "error");
    //         }
    //     };
    //     loadWorkcenters();
    // }, [storeId]);

    useEffect(() => {
        if (!storeId) return;
        const loadOrders = async () => {
            try {
                const res = await fetchUtilProductionOrders({ store_id: storeId });
                // console.log(res)
                const list = Array.isArray(res) ? res : (res?.orders || []);
                setProductionOrderOptions(list.map(po => ({
                    label: `Order #${po.productionorder_id} - ${po.description || 'No Description'}`,
                    value: po.productionorder_id.toString()
                })));
            } catch (err) {
                showToast("Could not retrieve production orders.", "error");
            }
        };
        loadOrders();
    }, [storeId]);

    const handleSelectWorkcenter = async (wcId) => {
        setSelectedWc(wcId);
        if (!wcId) return;
        setLoading(true);
        try {
            const ops = await client.postData(`/manufacturing/work-center-operations`, {
                storeId,
                wcId,
                productionorder_id: selectedPo,
            });
            // console.log(ops)
            setActiveOperations(Array.isArray(ops) ? ops : []);
        } catch (err) {
            showToast("Failed to fetch operations for this station.", "error");
            setActiveOperations([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectProductionOrder = async (poId) => {
        setSelectedPo(poId);
        setSelectedWc('');
        setActiveOperations([]);
        if (!poId) {
            setWorkcenterOptions([]);
            return;
        }
        try {
            const res = await client.fetchData(`/manufacturing/work-centers/${storeId}?productionorder_id=${poId}`);
            // console.log(res)
            const list = Array.isArray(res) ? res : (res?.workcenters || []);
            setWorkcenterOptions(list.map(w => ({
                label: `${w.name} - Cell #${w.workcenter_id}`,
                value: w.workcenter_id.toString()
            })));
        } catch (err) {
            showToast("Could not retrieve work centers for selected order.", "error");
            setWorkcenterOptions([]);
        }
    };

    // ACTION 1: Begin Execution (Transition to In Progress)
    const handleStartOp = async (operationId) => {
        try {
            const payload = {
                operation_id: operationId,
                status: 'In Progress',
                start_time_actual: new Date().toISOString(),
                store_id: storeId,
            };
            // console.log(payload)
            await client.postData(`/manufacturing/process-begin-execution`, payload);
            showToast("Operation marked as IN PROGRESS", "success");
            handleSelectWorkcenter(selectedWc);
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to start operation execution.", "error");
        }
    };

    // ACTION 2: Complete & Submit (Transition to Completed)
    const handleCompleteOp = async (operationId, targetQuantity) => {
        // Validation for produced quantity cap
        if (isLastOperation) {
            if (!producedQuantity || producedQuantity.trim() === '') {
                showToast("Produced Quantity field is required for the final opertion.", "error");
                return;
            }

            const qtyNum = parseFloat(producedQuantity);
            const maxQty = parseFloat(targetQuantity);
            const remaining = Math.max((isNaN(maxQty) ? 0 : maxQty) - (producedSoFar || 0), 0)

            if (isNaN(qtyNum) || qtyNum <= 0) {
                showToast("Produced quantity must be a positive number.", "error");
                return;
            }

            if (qtyNum > remaining + 1e-9) {
                showToast(
                    `Produced quantity cannot exceed remaining target (${remaining}). Already produced: ${producedSoFar}.`,
                    "error"
                );
                return;
            }

            if (!isNaN(maxQty) && qtyNum > maxQty) {
                showToast(`Produced quantity cannot exceed target output (${maxQty})`, "error");
                return
            }
        }

        try {
            const payload = {
                operation_id: operationId,
                status: 'Completed',
                end_time_actual: new Date().toISOString(),
                actual_setup_minutes: completionData.actualSetupMinutes ? parseFloat(completionData.actualSetupMinutes) : null,
                actual_run_minutes: completionData.actualRunMinutes ? parseFloat(completionData.actualRunMinutes) : null,
                scrap_quantity: completionData.scrapQuantity ? parseFloat(completionData.scrapQuantity) : 0,
                produced_quantity: producedQuantity ? parseFloat(producedQuantity) : null,
                notes: completionData.notes || null
            };
            // console.log(payload)
            await client.postData(`/manufacturing/production-operation/${operationId}/status`, payload);
            showToast("Operation completed successfully", "success");
            setExecutingOpId(null);
            setCompletionData({ actualSetupMinutes: '', actualRunMinutes: '', scrapQuantity: '0', notes: '' });
            setIsLastOperation(false);
            setProducedQuantity('');
            setProducedSoFar(0);
            handleSelectWorkcenter(selectedWc);
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to finalize operation.", "error");
        }
    };

    const startOpLogging = async (op) => {
        let isLast = false;
        let soFar = 0;
        try {
            const res = await client.fetchData(`/manufacturing/check-end-operation/${op.productionorder_id}`);
            if (res && res.msg === 'true') {
                isLast = true;
            }
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to check end operation status", "error");
        }

        if (isLast) {
            try {
                const soFarRes = await fetchProducedSoFar(op.productionorder_id);
                soFar = parseFloat(soFarRes?.sofar ?? 0) || 0;
            } catch (err) {
                console.error("Failed to fetch produced-so-far", err);
                showToast(err.response?.data?.detail || "Failed to fetch produced-so-far", "error");
                soFar = 0;
            }
        }
        setIsLastOperation(isLast);
        setProducedSoFar(soFar);
        setProducedQuantity('');
        setExecutingOpId(op.operation_id);
        setCompletionData({
            actualSetupMinutes: op.standard_setup_minutes ? op.standard_setup_minutes.toString() : '',
            actualRunMinutes: op.standard_run_minutes ? op.standard_run_minutes.toString() : '',
            scrapQuantity: '0',
            notes: ''
        });
    };

    return (
        <Card className="p-6 border-zinc-200 shadow-sm bg-white">
            <div className="border-b border-zinc-100 pb-3 mb-6 flex justify-between items-center">
                <div>
                    <h2 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
                        <Wrench size={20} className="text-blue-600" /> Workstation Floor Console
                    </h2>
                    <p className="text-xs text-zinc-500">Real-time shop floor execution and workstation time logging terminal.</p>
                </div>
            </div>

            {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="md:col-span-2">
                    <SearchableSelectBox
                        label="Active Workstation Station / Cell"
                        options={workcenterOptions}
                        value={selectedWc}
                        onChange={(e) => handleSelectWorkcenter(e.target.value)}
                        placeholder="Choose your station..."
                    />
                </div>
                {selectedWc && (
                    <div className="flex items-center justify-around bg-zinc-50 border border-zinc-200 rounded-lg p-2 text-center text-xs">
                        <div>
                            <span className="block text-zinc-400 font-bold uppercase text-[9px]">Queued</span>
                            <span className="text-sm font-bold text-amber-600">
                                {activeOperations.filter(o => ['Pending', 'Planned'].includes(o.status)).length}
                            </span>
                        </div>
                        <div className="border-r border-zinc-200 h-6"></div>
                        <div>
                            <span className="block text-zinc-400 font-bold uppercase text-[9px]">In Progress</span>
                            <span className="text-sm font-bold text-blue-600">
                                {activeOperations.filter(o => o.status === 'In Progress').length}
                            </span>
                        </div>
                    </div>
                )}
            </div> */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                    <SearchableSelectBox
                        label="Production Order"
                        options={productionOrderOptions}
                        value={selectedPo}
                        onChange={(e) => handleSelectProductionOrder(e.target.value)}
                        placeholder="Select Production Order..."
                    />
                </div>
                <div>
                    <SearchableSelectBox
                        label="Active Workstation Station / Cell"
                        options={workcenterOptions}
                        value={selectedWc}
                        onChange={(e) => handleSelectWorkcenter(e.target.value)}
                        placeholder="Choose your station..."
                        disabled={!selectedPo}
                    />
                </div>
                {selectedWc && (
                    <div className="flex items-center justify-around bg-zinc-50 border border-zinc-200 rounded-lg p-2 text-center text-xs">
                        <div>
                            <span className="block text-zinc-400 font-bold uppercase text-[9px]">Queued</span>
                            <span className="text-sm font-bold text-amber-600">
                                {activeOperations.filter(o => ['Pending', 'Planned'].includes(o.status)).length}
                            </span>
                        </div>
                        <div className="border-r border-zinc-200 h-6"></div>
                        <div>
                            <span className="block text-zinc-400 font-bold uppercase text-[9px]">In Progress</span>
                            <span className="text-sm font-bold text-blue-600">
                                {activeOperations.filter(o => o.status === 'In Progress').length}
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {loading ? (
                <div className="p-8 text-center text-zinc-500"><Loader2 className="animate-spin inline mr-2 text-blue-600" /> Fetching workstation queue...</div>
            ) : !selectedWc ? (
                <div className="p-8 border border-dashed border-zinc-200 rounded-xl text-center text-zinc-400 text-xs">
                    Select a workstation cell above to begin processing pending operations.
                </div>
            ) : activeOperations.length === 0 ? (
                <div className="p-8 border border-zinc-200 rounded-xl text-center text-zinc-500 text-xs bg-zinc-50">
                    No active operations currently queued for this workstation cell.
                </div>
            ) : (
                <div className="space-y-4">
                    {activeOperations.map((op) => (
                        <div key={op.operation_id} className="p-4 border border-zinc-200 rounded-xl bg-white hover:border-blue-200 transition-colors shadow-xs">
                            <div className="flex justify-between items-start mb-3">
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">Operation Seq #{op.sequence}</span>
                                        <span className="text-[10px] bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded font-mono">Order #{op.productionorder_id}</span>
                                    </div>
                                    <h4 className="text-base font-bold text-zinc-900">{op.description || 'Standard Production Step'}</h4>
                                    <div className="flex items-center gap-4 text-xs text-zinc-500 mt-1">
                                        <span>Target Output: <strong className="text-zinc-800">{op.target_quantity || 'N/A'} units</strong></span>
                                        {op.standard_run_minutes && (
                                            <span className="flex items-center gap-1">
                                                <Clock size={12} className="text-zinc-400" /> Std Run: <strong className="text-zinc-800">{op.standard_run_minutes}m</strong>
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border ${op.status === 'In Progress' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                    op.status === 'Completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                                    }`}>
                                    {op.status}
                                </span>
                            </div>

                            {executingOpId === op.operation_id ? (
                                <div className="mt-3 p-4 bg-zinc-50 rounded-lg border border-zinc-200 space-y-3">
                                    <div className="text-xs font-semibold text-zinc-700 flex items-center gap-1">
                                        <Clock size={14} className="text-blue-600" /> Log Final Operational Metrics
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        <Input label="Actual Setup (Mins)" type="number" value={completionData.actualSetupMinutes} onChange={(e) => setCompletionData(p => ({ ...p, actualSetupMinutes: e.target.value }))} />
                                        <Input label="Actual Run (Mins)" type="number" value={completionData.actualRunMinutes} onChange={(e) => setCompletionData(p => ({ ...p, actualRunMinutes: e.target.value }))} />
                                        <Input label="Scrap / Defect Qty" type="number" value={completionData.scrapQuantity} onChange={(e) => setCompletionData(p => ({ ...p, scrapQuantity: e.target.value }))} />
                                        {isLastOperation && (
                                            <div className="sm:col-span-3 space-y-1">
                                                <Input
                                                    label="Produced Quantity (this run)"
                                                    type="number"
                                                    value={producedQuantity}
                                                    onChange={(e) => setProducedQuantity(e.target.value)}
                                                    required={isLastOperation}
                                                    max={Math.max((op.target_quantity || 0) - producedSoFar, 0)}
                                                />
                                                <p className="text-[10px] text-zinc-500">
                                                    Already produced: <strong>{producedSoFar}</strong>
                                                    {' · '}
                                                    Remaining: <strong>{Math.max((op.target_quantity || 0) - producedSoFar, 0)}</strong>
                                                    {' / '}
                                                    Target: <strong>{op.target_quantity ?? 'N/A'}</strong>
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
                                        <Button onClick={() => setExecutingOpId(null)} className="text-xs bg-white border border-zinc-300 text-zinc-700 hover:bg-zinc-100 py-1.5 px-3">
                                            Cancel
                                        </Button>
                                        <Button onClick={() => handleCompleteOp(op.operation_id, op.target_quantity)} className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white py-1.5 px-4 flex items-center gap-1">
                                            <Check size={14} /> Submit & Complete
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="mt-3 flex justify-end gap-2 border-t border-zinc-100 pt-3">
                                    {['Pending', 'Planned'].includes(op.status) && (
                                        <Button onClick={() => handleStartOp(op.operation_id)} className="text-xs bg-blue-600 hover:bg-blue-700 text-white py-1.5 px-4 flex items-center gap-1 font-medium shadow-xs">
                                            <Play size={13} /> Begin Execution
                                        </Button>
                                    )}
                                    {op.status === 'In Progress' && (
                                        <Button onClick={() => startOpLogging(op)} className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white py-1.5 px-4 flex items-center gap-1 font-medium shadow-xs">
                                            <CheckCircle size={13} /> Log & Finish Step
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
};